/**
 * 대용량 데이터 캐시용 저장소. localStorage(약 5MB 고정)와 같은 동기 API를 쓰되 실제 보관은 IndexedDB.
 * - 앱 시작 시 `initDeviceCacheStorage()`로 전체를 메모리에 올린 뒤 동기 get/set으로 사용한다.
 * - 예전 localStorage 값은 처음 읽을 때 옮기고 원래 자리에서 지운다(키 나열은 두 저장소를 합쳐 보여 준다).
 * - 다른 탭에는 BroadcastChannel로 변경을 알리고, 그 탭에서 `storage` 이벤트를 똑같이 발생시킨다.
 * - IndexedDB를 못 쓰거나 초기화가 늦으면 이번 세션은 localStorage로 그대로 동작한다.
 */
import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';

const DB_NAME = 'ClassPlannerDeviceCache';
const DB_VERSION = 1;
const STORE = 'kv';
const CHANNEL = 'cp-device-cache-v1';
const INIT_TIMEOUT_MS = 2500;
/** 총량이 넘으면 오래 쓰지 않은 항목부터 지운다(문자 수 기준, UTF-16 약 2바이트/자) */
const MAX_CHARS = 40 * 1024 * 1024;
const PRUNE_TO_CHARS = 30 * 1024 * 1024;

type Row = { v: string; t: number };

const mem = new Map<string, Row>();
let mode: 'pending' | 'idb' | 'local' = 'pending';
let initPromise: Promise<void> | null = null;
let totalChars = 0;
let channel: BroadcastChannel | null = null;

const pendingPuts = new Map<string, Row>();
const pendingDeletes = new Set<string>();
let flushScheduled = false;

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;
function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = openDb().then((db) => {
      db.onclose = () => {
        dbPromise = null;
      };
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      return db;
    });
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

async function loadAll(): Promise<void> {
  const db = await getDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).openCursor();
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve();
        return;
      }
      const row = cursor.value as Row | undefined;
      if (row && typeof row.v === 'string') {
        mem.set(String(cursor.key), row);
        totalChars += row.v.length;
      }
      cursor.continue();
    };
  });
}

function scheduleFlush(): void {
  if (flushScheduled || mode !== 'idb') return;
  flushScheduled = true;
  window.setTimeout(() => {
    flushScheduled = false;
    void flush();
  }, 50);
}

async function flush(): Promise<void> {
  if (pendingPuts.size === 0 && pendingDeletes.size === 0) return;
  const puts = Array.from(pendingPuts.entries());
  const deletes = Array.from(pendingDeletes);
  pendingPuts.clear();
  pendingDeletes.clear();
  try {
    const db = await getDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      deletes.forEach((k) => store.delete(k));
      puts.forEach(([k, row]) => store.put(row, k));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    /* 저장 실패는 다음 읽기에서 캐시 miss → DB 조회로 회복 */
  }
}

function pruneIfNeeded(): void {
  if (totalChars <= MAX_CHARS) return;
  const oldest = Array.from(mem.entries()).sort((a, b) => a[1].t - b[1].t);
  for (const [k, row] of oldest) {
    if (totalChars <= PRUNE_TO_CHARS) break;
    mem.delete(k);
    totalChars -= row.v.length;
    pendingPuts.delete(k);
    pendingDeletes.add(k);
  }
  scheduleFlush();
}

function readLegacy(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeLegacy(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function dispatchStorageEvent(key: string, oldValue: string | null, newValue: string | null): void {
  try {
    window.dispatchEvent(new StorageEvent('storage', { key, oldValue, newValue }));
  } catch {
    /* ignore */
  }
}

function broadcast(key: string, value: string | null): void {
  try {
    channel?.postMessage({ key, value });
  } catch {
    /* ignore */
  }
}

function setMem(key: string, value: string, persist: boolean): string | null {
  const prev = mem.get(key);
  if (prev) totalChars -= prev.v.length;
  const row = { v: value, t: Date.now() };
  mem.set(key, row);
  totalChars += value.length;
  if (persist) {
    pendingDeletes.delete(key);
    pendingPuts.set(key, row);
    scheduleFlush();
    pruneIfNeeded();
  }
  return prev?.v ?? null;
}

function deleteMem(key: string, persist: boolean): string | null {
  const prev = mem.get(key);
  if (!prev) return null;
  mem.delete(key);
  totalChars -= prev.v.length;
  if (persist) {
    pendingPuts.delete(key);
    pendingDeletes.add(key);
    scheduleFlush();
  }
  return prev.v;
}

function listenOtherTabs(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (ev: MessageEvent<{ key?: unknown; value?: unknown }>) => {
    const key = typeof ev.data?.key === 'string' ? ev.data.key : '';
    if (!key) return;
    const value = typeof ev.data?.value === 'string' ? ev.data.value : null;
    const oldValue = value == null ? deleteMem(key, false) : setMem(key, value, false);
    dispatchStorageEvent(key, oldValue, value);
  };
}

/** 앱 시작 시 한 번. 실패·지연 시 이번 세션은 localStorage로 동작 */
export function initDeviceCacheStorage(): Promise<void> {
  if (initPromise) return initPromise;
  if (!hasWindow()) {
    mode = 'local';
    initPromise = Promise.resolve();
    return initPromise;
  }
  const load = loadAll().then(() => {
    if (mode === 'pending') {
      mode = 'idb';
      listenOtherTabs();
      pruneIfNeeded();
      void navigator.storage?.persist?.().catch(() => false);
    }
  });
  const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, INIT_TIMEOUT_MS));
  initPromise = Promise.race([load, timeout])
    .catch(() => undefined)
    .then(() => {
      if (mode === 'pending') {
        mode = 'local';
        mem.clear();
        totalChars = 0;
      }
    });
  return initPromise;
}

function localKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k) keys.push(k);
    }
  } catch {
    /* ignore */
  }
  return keys;
}

let keySnapshot: string[] | null = null;
function snapshotKeys(): string[] {
  if (!keySnapshot) {
    const set = new Set<string>(mem.keys());
    localKeys().forEach((k) => set.add(k));
    keySnapshot = Array.from(set);
    window.setTimeout(() => {
      keySnapshot = null;
    }, 0);
  }
  return keySnapshot;
}

/** localStorage와 같은 모양의 동기 API */
export const deviceCacheStorage = {
  getItem(key: string): string | null {
    if (!hasWindow()) return null;
    if (mode !== 'idb') return readLegacy(key);
    const hit = mem.get(key);
    if (hit) return hit.v;
    const legacy = readLegacy(key);
    if (legacy != null) {
      setMem(key, legacy, true);
      removeLegacy(key);
    }
    return legacy;
  },
  setItem(key: string, value: string): void {
    if (!hasWindow()) return;
    if (mode !== 'idb') {
      localStorageSetItemWithQuotaRecovery(key, value);
      return;
    }
    if (!mem.has(key)) keySnapshot = null;
    setMem(key, value, true);
    removeLegacy(key);
    broadcast(key, value);
  },
  removeItem(key: string): void {
    if (!hasWindow()) return;
    keySnapshot = null;
    removeLegacy(key);
    if (mode !== 'idb') return;
    if (deleteMem(key, true) != null) broadcast(key, null);
  },
  key(index: number): string | null {
    if (!hasWindow()) return null;
    if (mode !== 'idb') {
      try {
        return window.localStorage.key(index);
      } catch {
        return null;
      }
    }
    return snapshotKeys()[index] ?? null;
  },
  get length(): number {
    if (!hasWindow()) return 0;
    if (mode !== 'idb') {
      try {
        return window.localStorage.length;
      } catch {
        return 0;
      }
    }
    return snapshotKeys().length;
  },
};

/** 저장 성공 여부를 돌려주는 기존 `localStorageSetItemWithQuotaRecovery` 대체 */
export function deviceCacheSetItem(key: string, value: string): boolean {
  try {
    deviceCacheStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function flushDeviceCacheStorage(): Promise<void> {
  return flush();
}
